import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ChooseLanguage from "../Layout/Header/ChooseLanguage";

const NAV_ITEMS = [
  { key: "home", path: "/" },
  { key: "features", path: "/#features" },
  { key: "ourTeam", path: "/#team" },
  { key: "faqs", path: "/#faq" },
  { key: "contact", path: "/#contact" },
  { key: "admin", path: "/dashboard" },
];

const Navbar: React.FC = () => {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const [isMenuOpen, setMenuOpen] = useState(false);
  const handleToggle = () => setMenuOpen(!isMenuOpen);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [pathname]);

  // handleScroll
  useEffect(() => {
    const elementId = document.getElementById("navbar");
    if (!elementId) return;

    const updateNavbarBackground = () => {
      const isDark = document.documentElement.classList.contains("dark");
      if (window.scrollY > 100) {
        elementId.classList.add("is-sticky");
        elementId.style.backgroundColor = "";
      } else {
        elementId.classList.remove("is-sticky");
        elementId.style.backgroundColor = isDark 
          ? "rgba(10, 14, 25, 0.25)" 
          : "rgba(255, 255, 255, 0.25)";
      }
    };

    const handleScroll = () => {
      updateNavbarBackground();
    };

    // Initial check
    updateNavbarBackground();

    window.addEventListener("scroll", handleScroll);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <>
      <div
        className="fixed top-0 right-0 left-0 transition-all h-auto z-[5] py-[20px] backdrop-blur-sm"
        id="navbar"
      >
        <div className="container 2xl:max-w-[1320px] mx-auto px-[12px]">
          <div className="flex items-center relative flex-wrap lg:flex-nowrap justify-between lg:justify-start">
            <Link
              to="/"
              className="inline-block max-w-[130px] ltr:mr-[15px] rtl:ml-[15px]"
            >
              <img
                src="/images/logo-big.svg"
                alt="logo"
                className="inline-block dark:hidden"
                width={126}
                height={36}
              />
              <img
                src="/images/white-logo-big.svg"
                alt="logo"
                className="hidden dark:inline-block"
                width={126}
                height={36}
              />
            </Link>

            <button
              type="button"
              className="inline-block relative leading-none lg:hidden"
              onClick={handleToggle}
            >
              <span className="h-[3px] w-[30px] my-[5px] block bg-black dark:bg-white"></span>
              <span className="h-[3px] w-[30px] my-[5px] block bg-black dark:bg-white"></span>
              <span className="h-[3px] w-[30px] my-[5px] block bg-black dark:bg-white"></span>
            </button>

            {/* For Big Devices */}
            <div className="hidden lg:flex items-center grow basis-full">
              <ul className="flex ltr:ml-[30px] rtl:mr-[30px] ltr:xl:ml-[55px] rtl:xl:mr-[55px] flex-row gap-[30px] xl:gap-[50px]">
                {NAV_ITEMS.map((item) => (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className={`font-medium transition-all hover:text-primary-600 text-[15px] xl:text-md dark:text-gray-400 ${
                        pathname === item.path
                          ? "text-primary-600 dark:text-primary-600"
                          : ""
                      }`}
                    >
                      {t(`navbar.${item.key}`)}
                    </Link>
                  </li>
                ))}
              </ul>

              <div className="flex items-center ltr:ml-auto rtl:mr-auto gap-[15px]">
                <ChooseLanguage />
                
                <Link
                  to="/sign-in"
                  className="inline-block text-primary-500 lg:text-[15px] xl:text-[16px] py-[11px] px-[17px] rounded-md transition-all font-medium border border-primary-500 hover:text-white hover:bg-primary-500"
                >
                  <span className="inline-block relative ltr:pl-[25px] rtl:pr-[25px]">
                    <i className="material-symbols-outlined absolute ltr:left-0 rtl:right-0 top-1/2 -translate-y-1/2 !text-[20px]">
                      login
                    </i>
                    {t("navbar.login")}
                  </span>
                </Link>

                <Link
                  to="/sign-up"
                  className="inline-block lg:text-[15px] xl:text-[16px] py-[11px] px-[17px] bg-primary-500 text-white rounded-md transition-all font-medium hover:bg-primary-400"
                >
                  <span className="inline-block relative ltr:pl-[25px] rtl:pr-[25px]">
                    <i className="material-symbols-outlined absolute ltr:left-0 rtl:right-0 top-1/2 -translate-y-1/2 !text-[20px]">
                      person
                    </i>
                    {t("navbar.register")}
                  </span>
                </Link>
              </div>
            </div>

            {/* For Responsive */}
            <div
              className={`bg-white dark:bg-black mt-[20px] p-[20px] md:p-[30px] w-full lg:hidden ${
                isMenuOpen ? "block" : "hidden"
              }`}
              id="navbar-collapse"
            >
              <ul>
                {NAV_ITEMS.map((item) => (
                  <li
                    key={item.path}
                    className="my-[14px] md:my-[16px] first:mt-0 last:mb-0"
                  >
                    <Link
                      to={item.path}
                      className={`font-medium dark:text-primary-600 transition-all hover:text-primary-600 ${
                        pathname === item.path ? "text-primary-600" : ""
                      }`}
                    >
                      {t(`navbar.${item.key}`)}
                    </Link>
                  </li>
                ))}
              </ul>

              <div className="flex items-center gap-[15px] mt-[14px] md:mt-[16px]">
                <ChooseLanguage />
                
                <Link
                  to="/sign-in"
                  className="inline-block text-primary-500 lg:text-[15px] xl:text-[16px] py-[11px] px-[17px] rounded-md transition-all font-medium border border-primary-500 hover:text-white hover:bg-primary-500"
                >
                  <span className="inline-block relative ltr:pl-[25px] rtl:pr-[25px]">
                    <i className="material-symbols-outlined absolute ltr:left-0 rtl:right-0 top-1/2 -translate-y-1/2 !text-[20px]">
                      login
                    </i>
                    {t("navbar.login")}
                  </span>
                </Link>

                <Link
                  to="/sign-up"
                  className="inline-block lg:text-[15px] xl:text-[16px] py-[11px] px-[17px] bg-primary-500 text-white rounded-md transition-all font-medium hover:bg-primary-400"
                >
                  <span className="inline-block relative ltr:pl-[25px] rtl:pr-[25px]">
                    <i className="material-symbols-outlined absolute ltr:left-0 rtl:right-0 top-1/2 -translate-y-1/2 !text-[20px]">
                      person
                    </i>
                    {t("navbar.register")}
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;

